import {
	OrderPackage,
	type OrderSetupDetailResponseDto,
	OrderStatus,
} from "Infrastructure/Api/Api";
import { Resources } from "Infrastructure/Translation/Resources";
import { validateOrderFields } from "Order/Components/Form/Refine/validateOrderFields";
import { validateStatus } from "Order/Components/Form/Refine/validateStatus";
import type { TOptions } from "i18next";
import type { ObjPathProxy } from "ts-object-path";
import { z } from "zod";
import { getAssignPartnerFormSchema } from "./assignPartnerFormSchema";
import { getAssignUserFormSchema } from "./assignUserFormSchema";
import { getB2BPromoFormSchema } from "./b2bPromoFormSchema";
import { getCustomerEditFormSchema } from "./customerEditSchema";
import { getDateNextActionFormSchema } from "./dateNextActionFormSchema";
import { getGeneralFormSchema } from "./generalFormSchema";
import { getStatusFormSchema } from "./statusFormSchema";

const orderValidationMessagesResource = Resources.base.order.validationMessages;
const jobDurationDaysSchema = z.union([
	z.literal(""),
	z.coerce.number().int().min(1),
]);

export const getOrderEditSchema = (
	t: (
		resourcePath: ObjPathProxy<unknown, string>,
		options?: TOptions,
	) => string,
	orderSetup?: OrderSetupDetailResponseDto | null,
) => {
	return z
		.object({
			...getStatusFormSchema().shape,
			...getAssignPartnerFormSchema().shape,
			...getAssignUserFormSchema().shape,
			...getCustomerEditFormSchema().shape,
			...getDateNextActionFormSchema().shape,
			...getGeneralFormSchema().shape,
			...getB2BPromoFormSchema().shape,
			jobDurationDays: jobDurationDaysSchema,
			// "" = the clearable "None" option; normalized to null on submit
			packageType: z.nativeEnum(OrderPackage).or(z.literal("")).nullish(),
		})
		.refine(
			(data) => {
				return !(
					(data.status === OrderStatus.done ||
						data.status === OrderStatus.in_progress) &&
					!data.partnerId
				);
			},
			{
				message: t(orderValidationMessagesResource.partnerRequired),
				path: ["status"],
			},
		)
		.superRefine((data, ctx) => {
			validateStatus({ data, ctx, t });
			validateOrderFields(data, ctx, t, orderSetup);
		});
};

export type OrderEditModel = z.infer<ReturnType<typeof getOrderEditSchema>>;
